import React, { useState, useEffect } from 'react';
import { getHistory } from '../../services/websocketApi/history';
import './History.css';
import '../../styles/theme.css'; // Corrected import path for theme styles

/**
 * History component to display trading history (Rewritten)
 */
const History = () => {
  const [historyData, setHistoryData] = useState({
    history: [],
    pagination: { total: 0, page: 1, limit: 50, pages: 0 },
    message: '',
    status: false
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState({
    limit: 50,
    page: 1,
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    const fetchHistory = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await getHistory(filter);
        if (data && data.history && data.history.data) {
          setHistoryData({
            ...data,
            history: data.history.data || []
          });
        } else {
          setHistoryData(data || { history: [], pagination: { total: 0, page: 1, limit: 50, pages: 0 } });
        }
      } catch (err) {
        console.error('Error fetching history:', err);
        setError(err.message || 'Failed to fetch trading history');
      } finally {
        setIsLoading(false);
      }
    };
    fetchHistory();
  }, [filter]);

  const historyItems = Array.isArray(historyData?.history) ? historyData.history : [];

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilter(prevFilter => ({
      ...prevFilter,
      [name]: value,
      page: 1 // Reset to page 1 on filter change
    }));
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > historyData.pagination.pages) return;
    setFilter(prevFilter => ({ ...prevFilter, page: newPage }));
  };

  const formatNumber = (num, decimals = 5, defaultVal = '--') => {
    if (num === undefined || num === null) return defaultVal;
    const parsedNum = parseFloat(num);
    if (isNaN(parsedNum)) return defaultVal;
    return parsedNum.toLocaleString(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '--';
    try {
      const date = new Date(timestamp);
      return date.toLocaleString(undefined, {
        year: 'numeric', month: '2-digit', day: '2-digit', 
        hour: '2-digit', minute: '2-digit', second: '2-digit'
      });
    } catch (e) {
      return 'Invalid Date';
    }
  };

  const formatInstrument = (symbol) => {
    if (!symbol) return '--';
    const parts = symbol.split('.');
    return parts.length > 1 ? parts[parts.length - 1] : symbol;
  };

  const getTransactionTypeLabel = (type) => {
    const typeStyles = {
      'buy': 'HistoryComponent_transaction-buy',
      'sell': 'HistoryComponent_transaction-sell',
      // Add others if needed
    };
    const typeLabels = { 'buy': 'Buy', 'sell': 'Sell' }; // Add others
    const cssClass = typeStyles[type] || '';
    const label = typeLabels[type] || type;
    return <span className={`HistoryComponent_transaction ${cssClass}`}>{label}</span>;
  };

  const renderPagination = () => {
    const { page, pages, total } = historyData.pagination || { page: 1, pages: 0, total: 0 };
    if (pages <= 1) return null;
    return (
      <div className="HistoryComponent_pagination">
        <button
          className="HistoryComponent_pagination-button"
          disabled={page <= 1}
          onClick={() => handlePageChange(page - 1)}
        >
          Previous
        </button>
        <div className="HistoryComponent_pagination-info">
          Page {page} of {pages} (Total: {total})
        </div>
        <button
          className="HistoryComponent_pagination-button"
          disabled={page >= pages}
          onClick={() => handlePageChange(page + 1)}
        >
          Next
        </button>
      </div>
    );
  };

  const renderTableContent = () => {
    if (historyItems.length === 0) {
      return (
        <tr>
          <td colSpan="8" className="HistoryComponent_message">
            {historyData.message || "No trading history for the selected period"}
          </td>
        </tr>
      );
    }
    return historyItems.map((item, index) => {
      const pnlValue = item.pnl;
      const pnlClass = pnlValue > 0 ? 'HistoryComponent_td-pnl-profit' : pnlValue < 0 ? 'HistoryComponent_td-pnl-loss' : '';
      const statusClass = `HistoryComponent_status-${item.status?.toLowerCase()}`;
      return (
        <tr className="HistoryComponent_tr" key={item.createdAt + '-' + index}>
          <td className="HistoryComponent_td">{formatTime(item.createdAt)}</td>
          <td className="HistoryComponent_td">{formatInstrument(item.instrument)}</td>
          <td className="HistoryComponent_td">{getTransactionTypeLabel(item.type)}</td>
          <td className="HistoryComponent_td">{formatNumber(item.size, 0)}</td>
          <td className="HistoryComponent_td">{formatNumber(item.entryPrice)}</td>
          <td className="HistoryComponent_td">{formatNumber(item.exitPrice)}</td>
          <td className={`HistoryComponent_td ${pnlClass}`}>{pnlValue > 0 ? '+' : ''}{formatNumber(pnlValue)}</td>
          <td className={`HistoryComponent_td ${statusClass}`}>{item.status}</td>
        </tr>
      );
    });
  };

  return (
    <div className="HistoryComponent_container">
      {/* Filters */}
      <div className="HistoryComponent_filters">
        <div className="HistoryComponent_filter-group">
          <label htmlFor="startDate" className="HistoryComponent_filter-label">From:</label>
          <input
            type="date"
            id="startDate"
            name="startDate"
            value={filter.startDate}
            onChange={handleFilterChange}
            className="HistoryComponent_filter-input"
          />
        </div>
        <div className="HistoryComponent_filter-group">
          <label htmlFor="endDate" className="HistoryComponent_filter-label">To:</label>
          <input
            type="date"
            id="endDate"
            name="endDate"
            value={filter.endDate}
            onChange={handleFilterChange}
            className="HistoryComponent_filter-input"
          />
        </div>
        <div className="HistoryComponent_filter-group">
          <label htmlFor="limit" className="HistoryComponent_filter-label">Limit:</label>
          <select
            id="limit"
            name="limit"
            value={filter.limit}
            onChange={handleFilterChange}
            className="HistoryComponent_filter-select"
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>
      </div>

      {/* Table Area */}
      <div className="HistoryComponent_table-wrapper">
        {isLoading && (
          <div className="HistoryComponent_loading-overlay">
            <div className="HistoryComponent_spinner"></div>
          </div>
        )}
        {error && historyItems.length === 0 ? (
           <div className="HistoryComponent_message HistoryComponent_error">
             Error loading history: {error}
           </div>
        ) : (
          <table className="HistoryComponent_table">
            <thead className="HistoryComponent_thead">
              <tr className="HistoryComponent_tr">
                <th className="HistoryComponent_th">Date</th>
                <th className="HistoryComponent_th">Instrument</th>
                <th className="HistoryComponent_th">Type</th>
                <th className="HistoryComponent_th">Size</th>
                <th className="HistoryComponent_th">Entry</th>
                <th className="HistoryComponent_th">Exit</th>
                <th className="HistoryComponent_th">P/L</th>
                <th className="HistoryComponent_th">Status</th>
              </tr>
            </thead>
            <tbody className="HistoryComponent_tbody">
              {/* Render rows or empty/loading message */}
              {!isLoading && renderTableContent()}
            </tbody>
          </table>
        )}
        {/* Render loading message inside tbody only when loading initial data */}
        {isLoading && historyItems.length === 0 && (
           <table className="HistoryComponent_table">
            <tbody>
             <tr>
               <td colSpan="8" className="HistoryComponent_message">
                 Loading history...
               </td>
             </tr>
            </tbody>
           </table>
        )}
      </div>

      {/* Pagination */}
      {!isLoading && !error && historyItems.length > 0 && renderPagination()}

    </div>
  );
};

export default History; 